import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { findProduct, PRODUCTS, PLANNER_PAGES, formatINR, getProductVariantPrice } from "@/lib/products";
import { loadCatalogProducts } from "@/lib/catalog";
import { useCart } from "@/context/CartContext";
import { logEvent } from "@/lib/analytics";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Star, Check, Truck, Shield, PackageCheck, Heart, ChevronRight, Minus, Plus } from "lucide-react";
import { ProductCard } from "@/components/product/ProductCard";

const LIFE_OS_COVER_IMAGES = {
  "Soft Paperback": {
    Cocoa: [
      "/product/life-os-soft-cocoa-desk.jpg",
      "/product/life-os-soft-cocoa-standing.jpg",
    ],
    Sand: [
      "/product/life-os-soft-sand-desk.jpg",
      "/product/life-os-soft-sand-standing.jpg",
    ],
    Cream: [
      "/product/life-os-soft-cream-desk.jpg",
      "/product/life-os-soft-cream-standing.jpg",
    ],
    Stone: [
      "/product/life-os-soft-stone-desk.jpg",
      "/product/life-os-soft-stone-standing.jpg",
    ],
  },
  "Hard Binding": {
    Cocoa: [
      "/product/life-os-cocoa-desk.jpg",
      "/product/life-os-cocoa-standing.jpg",
    ],
    Sand: [
      "/product/life-os-sand-desk.jpg",
      "/product/life-os-sand-standing.jpg",
    ],
    Cream: [
      "/product/life-os-cream-desk.jpg",
      "/product/life-os-cream-standing.jpg",
    ],
    Stone: [
      "/product/life-os-stone-desk.jpg",
      "/product/life-os-stone-standing.jpg",
    ],
  },
};

export default function Product() {
  const { slug } = useParams();
  const [catalog, setCatalog] = useState(PRODUCTS);
  const product = catalog.find((p) => p.slug === slug || p.id === slug) || findProduct(slug) || PRODUCTS[0];
  const { addItem } = useCart();
  const navigate = useNavigate();

  const [mainIdx, setMainIdx] = useState(0);
  const mobileGalleryRef = useRef(null);
  const [size, setSize] = useState(product.format[0]);
  const [cover, setCover] = useState(product.cover[0]);
  const [personalization, setPersonalization] = useState(product.personalization?.[0] || "");
  const [pageColor, setPageColor] = useState(product.pageColors?.[0] || "");
  const [color, setColor] = useState(product.colors[0]);
  const [qty, setQty] = useState(1);
  const isComingSoon = product.id !== "life-os-planner";
  const isOutOfStock = product.stockStatus === "out_of_stock" || product.status === "hidden" || isComingSoon;

  useEffect(() => {
    let mounted = true;
    loadCatalogProducts().then((loaded) => {
      if (mounted) setCatalog(loaded);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setMainIdx(0);
    setSize(product.format?.[0] || "A5");
    setCover(product.defaultOptions?.cover || product.cover?.[0] || "Soft Paperback");
    setPersonalization(product.defaultOptions?.personalization || product.personalization?.[0] || "");
    setPageColor(product.defaultOptions?.pageColor || product.pageColors?.[0] || "");
    setColor(product.defaultOptions?.color || product.colors?.[0] || "Cream");
    setQty(1);
  }, [product.id, product.format, product.cover, product.personalization, product.pageColors, product.colors, product.defaultOptions]);

  useEffect(() => {
    setMainIdx(0);
  }, [cover, color]);

  const swatchColor = {
    Sand: "#D6C4A8",
    Cream: "#F0E5CD",
    Stone: "#B8AC9A",
    Cocoa: "#6B4E36",
    Gold: "#D4AF37",
  };

  const lifeOsGalleryPages = [
    ...PLANNER_PAGES.filter((page) => page.key === "cover"),
    ...PLANNER_PAGES.filter((page) => ["monthly", "weekly-dashboard", "daily-system"].includes(page.key)),
    ...PLANNER_PAGES.filter((page) => !["cover", "monthly", "weekly-dashboard", "daily-system"].includes(page.key)).slice(0, -4),
  ];
  const galleryImages = product.id === "life-os-planner"
    ? [
        ...(LIFE_OS_COVER_IMAGES[cover]?.[color] || LIFE_OS_COVER_IMAGES["Soft Paperback"].Cocoa),
        ...lifeOsGalleryPages.map((page) => page.img),
      ]
    : product.images?.length ? product.images : ["/product/life-os-planner-closeup.jpg"];
  const mobileThumbs = galleryImages.slice(0, 4);
  const remainingImages = Math.max(0, galleryImages.length - mobileThumbs.length);

  const related = catalog.filter((p) => p.id !== product.id && p.status !== "hidden").slice(0, 5);
  const selectedPrice = getProductVariantPrice(product, { size, cover, personalization, pageColor, color });
  const productOffers = [
    { code: "FREESHIP", title: "Free Delivery", text: "Use this code at checkout for free standard delivery." },
    { code: "NEWUSER15", title: "New User 15", text: "Get 15% off on your first order." },
    { code: "NC10", title: "NC10", text: "Get 10% off on your planner order." },
    { title: "Starting Price", text: "The Life OS Planner starts from INR 349." },
    { title: "Secure Online Payment", text: "Pay safely with UPI, cards, net banking, or wallet." },
  ];

  const handleAdd = () => {
    if (isOutOfStock) return;
    addItem(product, { size, cover, personalization, pageColor, color, image: galleryImages[0] }, qty);
  };
  const handleBuy = () => {
    if (isOutOfStock) return;
    logEvent("buy_now_click", { productId: product.id, name: product.name, priceINR: selectedPrice });
    addItem(product, { size, cover, personalization, pageColor, color, image: galleryImages[0] }, qty);
    navigate("/checkout");
  };
  const goToMobileImage = (index) => {
    const safeIndex = Math.max(0, Math.min(index, galleryImages.length - 1));
    setMainIdx(safeIndex);
    const gallery = mobileGalleryRef.current;
    if (gallery) {
      gallery.scrollTo({ left: safeIndex * gallery.clientWidth, behavior: "smooth" });
    }
  };
  const handleMobileGalleryScroll = (event) => {
    const width = event.currentTarget.clientWidth || 1;
    const index = Math.round(event.currentTarget.scrollLeft / width);
    setMainIdx(Math.max(0, Math.min(index, galleryImages.length - 1)));
  };
  const copyOfferCode = async (code) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`${code} copied`);
    } catch (error) {
      toast.error("Could not copy code");
    }
  };
  const OfferCards = ({ compact = false }) => (
    <div className={compact ? "flex gap-3 overflow-x-auto pb-1" : "space-y-3"}>
      {productOffers.map((offer) => (
        <div
          key={offer.code || offer.title}
          className={compact ? "w-[260px] shrink-0 rounded-lg border border-line bg-cream p-4" : "rounded-lg border border-line bg-cream p-4"}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-espresso">{offer.title}</div>
              <div className="mt-1 text-sm leading-relaxed text-muted_brown">{offer.text}</div>
            </div>
            {offer.code && (
              <button
                type="button"
                onClick={() => copyOfferCode(offer.code)}
                className="shrink-0 rounded-full border border-espresso px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-espresso"
              >
                Copy
              </button>
            )}
          </div>
          {offer.code && (
            <div className="mt-3 inline-flex rounded-md border border-dashed border-gold bg-champagne/30 px-3 py-1 text-xs font-semibold tracking-[0.16em] text-espresso">
              {offer.code}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div data-testid="product-page" className="pb-40 sm:pb-0">
      <div className="container-nc hidden pt-8 lg:block">
        <div className="flex items-center gap-2 text-xs text-muted_brown">
          <Link to="/" className="hover:text-espresso">Home</Link>
          <ChevronRight size={12} />
          <Link to="/shop" className="hover:text-espresso">Shop</Link>
          <ChevronRight size={12} />
          <span className="text-espresso">{product.name}</span>
        </div>
      </div>

      <section className="lg:hidden">
        <div className="px-4 pt-4">
          <div className="flex items-center gap-2">
            {product.badge && (
              <span className="border-b border-gold pb-0.5 text-[11px] font-medium uppercase tracking-[0.22em] text-gold">
                {product.badge}
              </span>
            )}
          </div>
          <h1 className="mt-3 font-serif text-[34px] leading-[1.05] text-espresso">
            {product.name}
          </h1>
          <div className="mt-3 flex items-center gap-2">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={13} className="fill-gold text-gold" strokeWidth={0} />
              ))}
            </div>
            <span className="text-sm text-muted_brown">
              {product.id === "life-os-planner" ? "4.9 rating (167 rating)" : `${product.rating} (${product.reviews.toLocaleString()} reviews)`}
            </span>
          </div>
        </div>

        <div className="mt-4 border-y border-line bg-cream">
          <div
            ref={mobileGalleryRef}
            onScroll={handleMobileGalleryScroll}
            className="relative flex aspect-[1.05/1] snap-x snap-mandatory overflow-x-auto scroll-smooth bg-ivory"
          >
            {galleryImages.map((src, index) => (
              <div key={src + index} className="relative h-full w-full shrink-0 snap-center">
                <img
                  src={src}
                  alt={product.name}
                  className="h-full w-full object-cover"
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchPriority={index === 0 ? "high" : "auto"}
                />
              </div>
            ))}
            <button
              aria-label="Wishlist"
              className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-line bg-cream text-espresso shadow-medium"
            >
              <Heart size={18} strokeWidth={1.6} />
            </button>
          </div>
          <div className="flex justify-center gap-1.5 py-3">
            {galleryImages.slice(0, 6).map((_, i) => (
              <button
                key={i}
                aria-label={`Show image ${i + 1}`}
                onClick={() => goToMobileImage(i)}
                className={`h-2 rounded-full transition-all ${mainIdx === i ? "w-5 bg-espresso" : "w-2 bg-line"}`}
              />
            ))}
          </div>
          <div className="flex gap-2 overflow-x-auto px-4 pb-4">
            {mobileThumbs.map((src, i) => {
              const showMore = remainingImages > 0 && i === mobileThumbs.length - 1;
              return (
                <button
                  key={src + i}
                  onClick={() => goToMobileImage(showMore ? mobileThumbs.length : i)}
                  data-testid={`thumb-mobile-premium-${i}`}
                  className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border bg-ivory ${
                    mainIdx === i || (showMore && mainIdx >= mobileThumbs.length) ? "border-espresso ring-1 ring-espresso" : "border-line"
                  }`}
                >
                  <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
                  {showMore && (
                    <span className="absolute inset-0 flex items-center justify-center bg-espresso/70 text-sm font-semibold text-cream">
                      +{remainingImages}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="border-b border-line px-4 py-5">
          <div className="flex items-baseline gap-3">
            <div className="text-3xl font-semibold leading-none text-espresso">{formatINR(selectedPrice)}</div>
            {product.compareAtINR && (
              <div className="text-sm text-muted_brown line-through">{formatINR(product.compareAtINR)}</div>
            )}
          </div>
          <ul className="mt-5 space-y-2.5">
            {product.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-[14px] leading-relaxed text-espresso">
                <Check size={14} className="mt-1 shrink-0 text-gold" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {product.colors.length > 0 && (
          <div className="border-b border-line px-4 py-5">
            <div className="text-[15px] text-espresso">
              Colour: <span className="font-semibold">{color}</span>
            </div>
            <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
              {product.colors.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  data-testid={`mobile-color-${c}`}
                  className={`w-28 shrink-0 rounded-lg border p-2 text-left transition-colors ${
                    color === c ? "border-espresso bg-ivory ring-1 ring-espresso" : "border-line bg-cream"
                  }`}
                >
                  <img
                    src={LIFE_OS_COVER_IMAGES[cover]?.[c]?.[0] || galleryImages[0]}
                    alt=""
                    className="h-20 w-full rounded-md object-cover"
                    loading="lazy"
                  />
                  <div className="mt-2 truncate text-sm font-semibold text-espresso">{c}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {product.cover.length > 1 && product.cover[0] !== "N/A" && (
          <div className="border-b border-line px-4 py-5">
            <div className="text-[15px] text-espresso">
              Cover: <span className="font-semibold">{cover}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {product.cover.map((c) => (
                <button
                  key={c}
                  onClick={() => setCover(c)}
                  data-testid={`mobile-cover-${c}`}
                  className={`rounded-lg border px-4 py-4 text-left transition-colors ${
                    cover === c ? "border-espresso bg-ivory ring-1 ring-espresso" : "border-line bg-cream"
                  }`}
                >
                  <div className="font-semibold text-espresso">{c}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {product.personalization?.length > 0 && (
          <div className="border-b border-line px-4 py-5">
            <div className="text-[15px] text-espresso">
              Personalization: <span className="font-semibold">{personalization}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {product.personalization.map((option) => (
                <button
                  key={option}
                  onClick={() => setPersonalization(option)}
                  data-testid={`mobile-personalization-${option}`}
                  className={`rounded-lg border px-4 py-4 text-left transition-colors ${
                    personalization === option ? "border-espresso bg-ivory ring-1 ring-espresso" : "border-line bg-cream"
                  }`}
                >
                  <div className="font-semibold text-espresso">{option}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {product.pageColors?.length > 0 && (
          <div className="border-b border-line px-4 py-5">
            <div className="text-[15px] text-espresso">
              Pages Colour: <span className="font-semibold">{pageColor}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {product.pageColors.map((option) => (
                <button
                  key={option}
                  onClick={() => setPageColor(option)}
                  data-testid={`mobile-page-color-${option}`}
                  className={`rounded-lg border px-4 py-4 text-left transition-colors ${
                    pageColor === option ? "border-espresso bg-ivory ring-1 ring-espresso" : "border-line bg-cream"
                  }`}
                >
                  <div className="font-semibold text-espresso">{option}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="container-nc hidden py-10 lg:grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">
        {/* Gallery */}
        <div className="space-y-6 sm:space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-[80px_1fr] gap-3 md:gap-4">
            <div className="hidden md:flex max-h-[720px] flex-col gap-3 overflow-y-auto pr-1">
              {galleryImages.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setMainIdx(i)}
                  data-testid={`thumb-${i}`}
                  className={`aspect-square rounded-lg overflow-hidden border ${
                    mainIdx === i ? "border-espresso" : "border-line"
                  }`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
            <div className="relative aspect-square sm:aspect-[4/5] bg-ivory rounded-xl sm:rounded-2xl overflow-hidden border border-line">
              <img
                src={galleryImages[mainIdx]}
                alt={product.name}
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
              />
            </div>
            <div className="flex md:hidden gap-2 overflow-x-auto pb-1">
              {mobileThumbs.map((src, i) => {
                const showMore = remainingImages > 0 && i === mobileThumbs.length - 1;
                return (
                  <button
                    key={src + i}
                    onClick={() => setMainIdx(i)}
                    data-testid={`thumb-mobile-${i}`}
                    className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border bg-ivory ${
                      mainIdx === i ? "border-espresso ring-1 ring-espresso" : "border-line"
                    }`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
                    {showMore && (
                      <span className="absolute inset-0 flex items-center justify-center bg-espresso/70 text-sm font-semibold text-cream">
                        +{remainingImages}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="hidden lg:block border-y border-line py-6">
            <div className="overline mb-4">Offers</div>
            <OfferCards />
          </div>

          <Accordion type="single" collapsible className="hidden lg:block border-t border-line">
            <AccordionItem value="desc" className="border-line">
              <AccordionTrigger data-testid="acc-desc" className="text-espresso uppercase text-xs tracking-[0.2em]">Description</AccordionTrigger>
              <AccordionContent className="text-muted_brown leading-relaxed">
                {product.description} Every page is designed for clarity, productivity and growth from vision setting to daily reflection.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="inside" className="border-line">
              <AccordionTrigger data-testid="acc-inside" className="text-espresso uppercase text-xs tracking-[0.2em]">What&rsquo;s Inside</AccordionTrigger>
              <AccordionContent>
                <ul className="text-muted_brown space-y-2">
                  {product.inside.map((i) => <li key={i}>- {i}</li>)}
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="specs" className="border-line">
              <AccordionTrigger data-testid="acc-specs" className="text-espresso uppercase text-xs tracking-[0.2em]">Specifications</AccordionTrigger>
              <AccordionContent>
                <dl className="text-sm">
                  {Object.entries(product.specs).map(([k,v]) => (
                    <div key={k} className="flex justify-between py-2 border-b border-line/50">
                      <dt className="text-muted_brown">{k}</dt>
                      <dd className="text-espresso">{v}</dd>
                    </div>
                  ))}
                </dl>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="ship" className="border-line">
              <AccordionTrigger data-testid="acc-ship" className="text-espresso uppercase text-xs tracking-[0.2em]">Shipping &amp; Issue Support</AccordionTrigger>
              <AccordionContent className="text-muted_brown">
                Free shipping across India on orders over INR 999. Returns are accepted only if the delivered product is wrong or an item is missing from your order.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

        </div>

        {/* Info */}
        <div className="flex flex-col">
          {product.badge && (
            <span className="overline text-gold">{product.badge}</span>
          )}
          {isOutOfStock && <span className="overline text-red-600">{isComingSoon ? "Coming soon" : "Out of stock"}</span>}
          <h1 className="font-serif text-4xl md:text-5xl text-espresso leading-tight mt-3">
            {product.name}
          </h1>
          <div className="flex items-center gap-3 mt-4">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_,i) => <Star key={i} size={14} className="fill-gold text-gold" strokeWidth={0}/>)}
            </div>
            <span className="text-sm text-muted_brown">
              {product.id === "life-os-planner" ? "4.9 rating (167 rating)" : `${product.rating} (${product.reviews.toLocaleString()} reviews)`}
            </span>
          </div>

          <div className="mt-6 flex items-baseline gap-3">
            <div className="text-3xl font-medium text-espresso whitespace-nowrap leading-none">{formatINR(selectedPrice)}</div>
            {product.compareAtINR && (
              <div className="text-muted_brown line-through">{formatINR(product.compareAtINR)}</div>
            )}
          </div>

          <p className="order-20 lg:order-none mt-5 text-muted_brown leading-relaxed">{product.description}</p>

          <ul className="order-21 lg:order-none mt-6 space-y-2">
            {product.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-espresso">
                <Check size={14} className="text-gold mt-1" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          {product.colors.length > 0 && (
            <div className="order-10 lg:order-none mt-8">
              <div className="overline mb-3">Color · <span className="text-espresso">{color}</span></div>
              <div className="flex gap-3">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    data-testid={`color-${c}`}
                    aria-label={c}
                    className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-105 ${
                      color === c ? "border-espresso" : "border-line"
                    }`}
                    style={{ backgroundColor: swatchColor[c] || "#EAE4D9" }}
                  />
                ))}
              </div>
            </div>
          )}

          {product.cover.length > 1 && product.cover[0] !== "N/A" && (
            <div className="order-11 lg:order-none mt-6">
              <div className="overline mb-3">Cover</div>
              <div className="flex gap-2">
                {product.cover.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCover(c)}
                    data-testid={`cover-${c}`}
                    className={`px-6 py-2.5 rounded-full text-sm transition-colors ${
                      cover === c
                        ? "bg-espresso text-cream"
                        : "border border-line text-espresso hover:border-espresso"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.personalization?.length > 0 && (
            <div className="order-12 lg:order-none mt-6">
              <div className="overline mb-3">Personalization</div>
              <div className="flex flex-wrap gap-2">
                {product.personalization.map((option) => (
                  <button
                    key={option}
                    onClick={() => setPersonalization(option)}
                    data-testid={`personalization-${option}`}
                    className={`px-6 py-2.5 rounded-full text-sm transition-colors ${
                      personalization === option
                        ? "bg-espresso text-cream"
                        : "border border-line text-espresso hover:border-espresso"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.pageColors?.length > 0 && (
            <div className="order-13 lg:order-none mt-6">
              <div className="overline mb-3">Pages Colour</div>
              <div className="flex flex-wrap gap-2">
                {product.pageColors.map((option) => (
                  <button
                    key={option}
                    onClick={() => setPageColor(option)}
                    data-testid={`page-color-${option}`}
                    className={`px-6 py-2.5 rounded-full text-sm transition-colors ${
                      pageColor === option
                        ? "bg-espresso text-cream"
                        : "border border-line text-espresso hover:border-espresso"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.format.length > 0 && product.format[0] !== "N/A" && (
            <div className="order-14 lg:order-none mt-6">
              <div className="overline mb-3">Size</div>
              <div className="flex gap-2">
                {product.format.map((f) => (
                  <button
                    key={f}
                    onClick={() => setSize(f)}
                    data-testid={`size-${f}`}
                    disabled={product.format.length === 1}
                    className={`px-6 py-2.5 rounded-full text-sm transition-colors ${
                      size === f
                        ? "bg-espresso text-cream"
                        : "border border-line text-espresso hover:border-espresso"
                    } disabled:cursor-default`}
                  >
                    {product.format.length === 1 ? `Standard ${f}` : f}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="order-30 lg:order-none mt-8 hidden sm:flex items-center gap-4">
            <div className="flex items-center border border-line rounded-full">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                data-testid="qty-decrease"
                className="w-10 h-10 flex items-center justify-center text-espresso hover:bg-ivory rounded-l-full"
              >
                <Minus size={14} />
              </button>
              <span data-testid="qty-value" className="w-10 text-center text-espresso">{qty}</span>
              <button
                onClick={() => setQty(qty + 1)}
                data-testid="qty-increase"
                className="w-10 h-10 flex items-center justify-center text-espresso hover:bg-ivory rounded-r-full"
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              onClick={handleAdd}
              data-testid="add-to-cart-btn"
              disabled={isOutOfStock}
              className="btn-outline flex-1 disabled:opacity-50"
            >
              {isComingSoon ? "Coming Soon" : isOutOfStock ? "Out of Stock" : "Add to Cart"}
            </button>
            <button
              aria-label="Wishlist"
              data-testid="wishlist-btn"
              className="w-11 h-11 rounded-full border border-line flex items-center justify-center text-espresso hover:border-espresso"
            >
              <Heart size={16} strokeWidth={1.5} />
            </button>
          </div>

          <button
            onClick={handleBuy}
            data-testid="buy-now-btn"
            disabled={isOutOfStock}
            className="btn-primary order-31 lg:order-none hidden sm:flex w-full mt-3 disabled:opacity-50"
          >
            {isComingSoon ? "Coming Soon" : isOutOfStock ? "Currently Unavailable" : "Buy Now"}
          </button>

          <div className="order-40 lg:order-none mt-8 grid grid-cols-3 gap-3 text-xs">
            {[
              { i: Truck, t: "Free Shipping", d: "Over ₹999" },
              { i: PackageCheck, t: "Issue Returns", d: "Wrong or missing item" },
              { i: Shield, t: "Secure Checkout", d: "Encrypted" },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="flex items-start gap-2 p-3 border border-line rounded-lg">
                <Icon size={16} strokeWidth={1.5} className="text-espresso mt-0.5" />
                <div>
                  <div className="text-espresso font-medium">{t}</div>
                  <div className="text-muted_brown">{d}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Accordions */}
          <Accordion type="single" collapsible className="hidden">
            <AccordionItem value="desc" className="border-line">
              <AccordionTrigger data-testid="acc-desc" className="text-espresso uppercase text-xs tracking-[0.2em]">Description</AccordionTrigger>
              <AccordionContent className="text-muted_brown leading-relaxed">
                {product.description} Every page is designed for clarity, productivity and growth — from vision setting to daily reflection.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="inside" className="border-line">
              <AccordionTrigger data-testid="acc-inside" className="text-espresso uppercase text-xs tracking-[0.2em]">What&rsquo;s Inside</AccordionTrigger>
              <AccordionContent>
                <ul className="text-muted_brown space-y-2">
                  {product.inside.map((i) => <li key={i}>· {i}</li>)}
                </ul>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="specs" className="border-line">
              <AccordionTrigger data-testid="acc-specs" className="text-espresso uppercase text-xs tracking-[0.2em]">Specifications</AccordionTrigger>
              <AccordionContent>
                <dl className="text-sm">
                  {Object.entries(product.specs).map(([k,v]) => (
                    <div key={k} className="flex justify-between py-2 border-b border-line/50">
                      <dt className="text-muted_brown">{k}</dt>
                      <dd className="text-espresso">{v}</dd>
                    </div>
                  ))}
                </dl>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="ship" className="border-line">
              <AccordionTrigger data-testid="acc-ship" className="text-espresso uppercase text-xs tracking-[0.2em]">Shipping &amp; Returns</AccordionTrigger>
              <AccordionContent className="text-muted_brown">
                Free shipping across India on orders over INR 999. Returns are accepted only if the delivered product is wrong or an item is missing from your order.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="hidden">
            {[
              { label: "Made For", text: "Daily planning, weekly resets, goals, habit tracking, money checks, and reflection in one B5 planner." },
              { label: "Cover Choice", text: `${cover} in ${color}, with optional name personalization on the front cover.` },
              { label: "Pages", text: `${pageColor || "B/W"} inner pages with structured layouts for focused planning.` },
              { label: "Delivery", text: "Packed carefully and shipped across India with secure online checkout." },
            ].map((item) => (
              <div key={item.label} className="border border-line bg-ivory/50 rounded-lg p-4">
                <div className="overline mb-2">{item.label}</div>
                <p className="text-sm leading-relaxed text-muted_brown">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-nc lg:hidden pb-10">
        <div className="border-y border-line py-6">
          <div className="overline mb-4">Offers</div>
          <OfferCards compact />
        </div>

        <Accordion type="single" collapsible className="mt-6 border-t border-line">
          <AccordionItem value="desc" className="border-line">
            <AccordionTrigger className="text-espresso uppercase text-xs tracking-[0.2em]">Description</AccordionTrigger>
            <AccordionContent className="text-muted_brown leading-relaxed">
              {product.description} Every page is designed for clarity, productivity and growth from vision setting to daily reflection.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="inside" className="border-line">
            <AccordionTrigger className="text-espresso uppercase text-xs tracking-[0.2em]">What&rsquo;s Inside</AccordionTrigger>
            <AccordionContent>
              <ul className="text-muted_brown space-y-2">
                {product.inside.map((i) => <li key={i}>- {i}</li>)}
              </ul>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="specs" className="border-line">
            <AccordionTrigger className="text-espresso uppercase text-xs tracking-[0.2em]">Specifications</AccordionTrigger>
            <AccordionContent>
              <dl className="text-sm">
                {Object.entries(product.specs).map(([k,v]) => (
                  <div key={k} className="flex justify-between gap-4 py-2 border-b border-line/50">
                    <dt className="text-muted_brown">{k}</dt>
                    <dd className="text-right text-espresso">{v}</dd>
                  </div>
                ))}
              </dl>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="ship" className="border-line">
            <AccordionTrigger className="text-espresso uppercase text-xs tracking-[0.2em]">Shipping &amp; Issue Support</AccordionTrigger>
            <AccordionContent className="text-muted_brown">
              Free shipping across India on orders over INR 999. Returns are accepted only if the delivered product is wrong or an item is missing from your order.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-line bg-cream px-4 pt-3 pb-[calc(12px+env(safe-area-inset-bottom))] shadow-[0_-10px_30px_rgba(59,43,32,0.16)] sm:hidden">
        <div className="mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-line bg-ivory px-3 py-3 shadow-soft">
          <div className="mr-1 min-w-[82px]">
            <div className="text-[9px] uppercase tracking-[0.18em] text-muted_brown">Total</div>
            <div className="whitespace-nowrap text-lg font-semibold leading-tight text-espresso">{formatINR(selectedPrice)}</div>
          </div>
          <button
            onClick={handleAdd}
            disabled={isOutOfStock}
            className="flex-1 rounded-full border border-espresso bg-cream px-3 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-espresso shadow-sm disabled:opacity-50"
          >
            Add Cart
          </button>
          <button
            onClick={handleBuy}
            disabled={isOutOfStock}
            className="flex-1 rounded-full bg-espresso px-3 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-cream shadow-sm disabled:opacity-50"
          >
            Buy Now
          </button>
        </div>
      </div>

      {/* Related */}
      <section className="container-nc py-16 border-t border-line">
        <h2 className="font-serif text-3xl md:text-4xl text-espresso mb-10">You May Also Like</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {related.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>
    </div>
  );
}
